import { useEffect, useCallback, useRef } from 'react';

interface ProtectionOptions {
  enabled?: boolean;
  allowList?: string[];
  showToast?: (message: string, type: 'warning' | 'error') => void;
}

export function useContentProtection(options: ProtectionOptions = {}) {
  const { enabled = true, allowList = [], showToast } = options;
  const isProtectionActiveRef = useRef(enabled);

  useEffect(() => {
    isProtectionActiveRef.current = enabled;
  }, [enabled]);

  const isInAllowList = useCallback((target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Element)) return false;
    
    return allowList.some(selector => {
      try {
        const elements = document.querySelectorAll(selector);
        return Array.from(elements).some(el => el.contains(target as Node));
      } catch {
        return false;
      }
    });
  }, [allowList]);

  const showProtectionMessage = useCallback((message: string) => {
    if (showToast) {
      showToast(message, 'warning');
    }
  }, [showToast]);

  useEffect(() => {
    if (!enabled) return;

    const handleContextMenu = (e: MouseEvent) => {
      if (isInAllowList(e.target)) return;
      e.preventDefault();
      showProtectionMessage('右键菜单已禁用，内容受保护');
    };

    const handleCopy = (e: ClipboardEvent) => {
      if (isInAllowList(e.target)) return;
      e.preventDefault();
      showProtectionMessage('复制功能已禁用，内容受版权保护');
    };

    const handleCut = (e: ClipboardEvent) => {
      if (isInAllowList(e.target)) return;
      e.preventDefault();
      showProtectionMessage('剪切功能已禁用');
    };

    const handleSelectStart = (e: Event) => {
      if (isInAllowList(e.target)) return;
      e.preventDefault();
    };

    const handleDragStart = (e: DragEvent) => {
      if (isInAllowList(e.target)) return;
      e.preventDefault();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isInAllowList(e.target)) return;

      const isCopyShortcut = (e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C');
      const isCutShortcut = (e.ctrlKey || e.metaKey) && (e.key === 'x' || e.key === 'X');
      const isSelectAllShortcut = (e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A');
      const isPrintShortcut = (e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P');

      if (isCopyShortcut) {
        e.preventDefault();
        showProtectionMessage('复制功能已禁用，内容受版权保护');
      } else if (isCutShortcut) {
        e.preventDefault();
        showProtectionMessage('剪切功能已禁用');
      } else if (isSelectAllShortcut) {
        e.preventDefault();
        showProtectionMessage('全选功能已禁用');
      } else if (isPrintShortcut) {
        e.preventDefault();
        showProtectionMessage('打印功能已禁用');
      }
    };

    document.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('copy', handleCopy, true);
    document.addEventListener('cut', handleCut, true);
    document.addEventListener('selectstart', handleSelectStart, true);
    document.addEventListener('dragstart', handleDragStart, true);
    document.addEventListener('keydown', handleKeyDown, true);

    const style = document.createElement('style');
    style.id = 'content-protection-style';
    style.textContent = `
      * {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        -webkit-touch-callout: none !important;
      }
      ${allowList.map(selector => `${selector}, ${selector} *`).join(', ')} {
        -webkit-user-select: text !important;
        -moz-user-select: text !important;
        -ms-user-select: text !important;
        user-select: text !important;
        -webkit-touch-callout: default !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('copy', handleCopy, true);
      document.removeEventListener('cut', handleCut, true);
      document.removeEventListener('selectstart', handleSelectStart, true);
      document.removeEventListener('dragstart', handleDragStart, true);
      document.removeEventListener('keydown', handleKeyDown, true);
      
      const existingStyle = document.getElementById('content-protection-style');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [enabled, isInAllowList, showProtectionMessage]);

  return {
    isProtectionActive: enabled,
    isInAllowList,
  };
}
